import React, { FC } from "react";
import { navigate } from "gatsby";
import { useLocation } from "@reach/router";
import { Banner } from "@ifgengineering/component-library";
import { peekAnchoringDeal } from "@helpers/loginRedirect";
import type { DealAnchoredView } from "@components/OnboardingV2B/progress";
import {
  isActionableRequirement,
  REQUIREMENT_PATH,
  V2B_FALLBACK_RETURN_PATH,
  V2B_PRE_APP_PATH,
} from "@components/OnboardingV2B/routes";
import { useDealAnchoredView } from "@components/OnboardingV2B/useDealAnchoredView";
import { useWebOnboardingV2B } from "@components/OnboardingV2B/useWebOnboardingV2B";
import { useIsDealOnboardingPromptShown } from "@components/OnboardingV2B/DealOnboardingPrompt";
import {
  destinationOf,
  toAnalyticsNextStep,
} from "@components/OnboardingV2B/onboardingAnalytics";
import { rememberEntryPoint } from "@components/OnboardingV2B/onboardingAnalyticsMemory";
import { useOnboardingAnalyticsContext } from "@components/OnboardingV2B/useOnboardingAnalyticsContext";
import useTrackEvent from "@hooks/useTrackEvent";
import {
  DealAnchoredBannerAction,
  DealAnchoredBannerSurface,
} from "./CompleteOnboardingBanner.styled";

const DEAL_ANCHORED_TEXT = "Finish setting up your account to invest.";

const stripTrailingSlash = (path: string): string => path.replace(/\/$/, "");

/** The deal that anchored onboarding, or on that very page the step its card offers. */
const dealAnchoredTarget = (
  progress: DealAnchoredView | null,
  pathname: string
): string | null => {
  if (!progress?.appUnlocked) return V2B_PRE_APP_PATH;
  const dealTarget = peekAnchoringDeal() ?? V2B_FALLBACK_RETURN_PATH;
  if (dealTarget !== stripTrailingSlash(pathname)) return dealTarget;
  const next = progress?.nextRequirement;
  return next && isActionableRequirement(next) ? REQUIREMENT_PATH[next] : null;
};

export const CompleteOnboardingBanner: FC = () => {
  const { isActive: isDealAnchored } = useWebOnboardingV2B();
  const progress = useDealAnchoredView();
  const { pathname } = useLocation();
  const isShownByDealPage = useIsDealOnboardingPromptShown();
  const target = isDealAnchored ? dealAnchoredTarget(progress, pathname) : null;
  const analytics = useOnboardingAnalyticsContext({
    surface: target === V2B_PRE_APP_PATH ? "preApp" : "dealPage",
    entryPoint: "homepage",
  });
  const trackBannerClicked = useTrackEvent("onboardingContinueBannerClicked");

  const continueOnboarding = (path: string): void => {
    const destination = destinationOf(path);
    // A step opens from the banner only on the anchoring deal's own page.
    rememberEntryPoint(destination === "step" ? "deal_details" : "homepage");
    if (progress) {
      trackBannerClicked({
        ...analytics,
        next_step: toAnalyticsNextStep(progress.nextRequirement),
        destination,
      });
    }
    navigate(path);
  };

  if (isDealAnchored) {
    if (isShownByDealPage) return null;

    return (
      <DealAnchoredBannerSurface data-testid="deal-anchored-onboarding-banner">
        <span>{DEAL_ANCHORED_TEXT}</span>
        {target ? (
          <DealAnchoredBannerAction
            type="button"
            data-testid="complete-onboarding-link"
            onClick={() => continueOnboarding(target)}
          >
            Continue onboarding
          </DealAnchoredBannerAction>
        ) : null}
      </DealAnchoredBannerSurface>
    );
  }

  return (
    <Banner
      testId="complete-onboarding-link"
      text="You need to complete your investor profile to be able to invest or view deals."
      CTAText="Complete Investor Profile"
      onClickCTA={() => navigate("/app/onboarding")}
    />
  );
};
